import { Backdrop, ButtonBase, styled } from '@ori-ui/mui';
import { shouldForwardProp, shouldNotForwardProp } from '@ori/presentation-utils';

import { SEARCH_INPUT_HEIGHT } from '../../constants';
import type { AppConfig } from '../../models';
import { getSearchInputTopAttribute } from '../../utils/getMobileCssVariable';
import { SEARCH_INPUT_BOX_HEIGHT } from './constants';

export interface SearchInputRootProps extends Pick<AppConfig, 'showSearchOnScroll'> {
  isTopBannerVisible: boolean;
  isVisibleOnMobile: boolean;
  isFocused: boolean;
  showOnlyOn: 'desktop' | 'mobile';
}

export const Inner = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  background: theme.palette.common.white,
  width: '100%',
  borderRadius: 100,
  [theme.breakpoints.up('md')]: {
    borderRadius: theme.spacing(5),
    margin: '0 auto',
    maxWidth: '900px',
  },
}));

export const SearchInputRoot = styled('div', {
  shouldForwardProp: shouldForwardProp(),
})<SearchInputRootProps>(
  ({ isTopBannerVisible, isVisibleOnMobile, showSearchOnScroll, showOnlyOn, isFocused, theme }) => ({
    background: theme.palette.common.white,
    boxSizing: 'border-box',
    display: isVisibleOnMobile ? 'flex' : 'none',
    height: SEARCH_INPUT_HEIGHT,
    justifySelf: 'center',
    left: 0,
    padding: theme.spacing(0, 3, 3, 3),
    position: 'fixed',
    top: getSearchInputTopAttribute({ isTopBannerVisible, showSearchOnScroll }),
    width: '100%',
    zIndex: isFocused ? theme.zIndex.appBar + 2 : theme.zIndex.speedDial,
    justifyContent: isVisibleOnMobile ? 'center' : undefined,

    [theme.breakpoints.up('md')]: {
      background: 'transparent',
      display: showOnlyOn === 'desktop' ? 'block' : 'none',
      height: SEARCH_INPUT_BOX_HEIGHT,
      padding: 0,
      position: 'static',
    },
  }),
);

export const SearchInputBox = styled('div')(({ theme }) => ({
  alignItems: 'center',
  background: theme.palette.grey.A100,
  borderRadius: 100,
  boxSizing: 'border-box',
  color: theme.palette.grey[600],
  display: 'flex',
  fontSize: theme.typography.pxToRem(23),
  height: SEARCH_INPUT_BOX_HEIGHT,
  overflow: 'hidden',
  paddingLeft: theme.spacing(3),
  width: '100%',

  [theme.breakpoints.up('md')]: {
    height: SEARCH_INPUT_BOX_HEIGHT,
    visibility: 'visible',
  },
}));

export const SearchInputInput = styled('input')(({ theme }) => ({
  background: theme.palette.grey.A100,
  border: 'none',
  color: theme.palette.text.primary,
  fontSize: theme.typography.pxToRem(16),
  height: 'auto',
  lineHeight: 1.5,
  marginLeft: 8,
  width: '100%',
  textOverflow: 'ellipsis',

  '&::placeholder': {
    color: theme.palette.grey.A700,
  },
}));

export const SearchInputClear = styled(ButtonBase, {
  shouldForwardProp: shouldNotForwardProp(['isFocused']),
})<{
  isFocused: boolean;
}>(({ isFocused, theme }) => ({
  height: 23,
  marginRight: theme.spacing(3),
  width: 23,

  [theme.breakpoints.up('md')]: {
    fontSize: theme.typography.pxToRem(20),
    marginRight: theme.spacing(2),

    visibility: 'hidden',

    ...(isFocused && {
      visibility: 'visible',
    }),
  },
}));

export const InputBackdrop = styled(Backdrop)({
  position: 'fixed',
  zIndex: '-1',
  width: '100vw',
  height: '100vh',
});
